import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const SESSION_KEY = "mercago_session_id";

export function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : "guest-" + Math.random().toString(36).slice(2) + Date.now();
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers["X-Session-Id"] = getSessionId();
  const token = localStorage.getItem("mercago_jwt");
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      // Only clear the token if we had one — an anonymous 401 is a signal
      // the user needs to log in but should not wipe an unrelated session.
      const hadToken = !!localStorage.getItem("mercago_jwt");
      if (hadToken) {
        localStorage.removeItem("mercago_jwt");
        window.dispatchEvent(new CustomEvent("mercago:unauthenticated"));
      }
    }
    return Promise.reject(error);
  }
);

/** Prefix a backend-relative image path with BACKEND_URL, or return as-is if absolute. */
export function imageUrl(u) {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  return `${BACKEND_URL}${u}`;
}

export function formatEUR(n) {
  if (typeof n !== "number") return "";
  return n.toFixed(2).replace(".", ",") + " €";
}

/**
 * Returns the browser's IANA timezone (e.g. "Europe/Madrid", "America/New_York").
 * Falls back to "Europe/Madrid" if the runtime doesn't expose it (very old browsers).
 * Used to render PDF receipts/reports in the reader's local wall-clock time.
 */
export function getBrowserTz() {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Madrid"
    );
  } catch {
    return "Europe/Madrid";
  }
}

/**
 * Fetch an admin PDF report as a blob and trigger a browser download.
 * The Authorization + X-Session-Id headers ride along via the axios instance.
 * @param {string} path e.g. "/admin/reports/shipments-pending.pdf"
 * @param {string} filename download filename shown to the user
 */
export async function downloadAdminPdf(path, filename) {
  const resp = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(
    new Blob([resp.data], { type: "application/pdf" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
